import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { graphSlice, DEFAULT_GRAPH_STATE, GRAPH_PREFERENCE_BASE, selectGraphPanel } from "connected-components/dashboard/store/graph-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"

// Pure slice reader — does NOT sync preferences. GraphWrapper calls useSyncGraphPreferences once;
// calling it here would make every consumer re-sync and crash outside a PreferenceContext (Storybook).
export function useGraphData(initialSelectedTemplate?: string) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(GRAPH_PREFERENCE_BASE)

  const graphInstance = useAppSelector((s) => selectGraphPanel(s, dashboardId, panelKey))
  const graphState = graphInstance ?? DEFAULT_GRAPH_STATE
  // Read the raw (un-coalesced) instance so GraphWrapper can hold the template tree until the slice
  // is seeded — otherwise nested consumers paint DEFAULT before the config seed lands.
  const isGraphInitialized = graphInstance != null

  // Layout effect for parity with the other panel seeds. Graph doesn't omit its field from the
  // fallback, so a saved template still overrides this seed; the phase only makes ordering deterministic.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for a template before latching, or a config arriving on a later render never seeds.
    if (initialSelectedTemplate == null) return
    seeded.current = true
    dispatch(graphSlice.actions.initFromModuleConfig({ dashboardId, panelId: panelKey, selectedTemplate: initialSelectedTemplate }))
  }, [dashboardId, panelKey, dispatch, initialSelectedTemplate])

  const graphUpdaters = useMemo(() => ({
    setSelectedTemplate: (template: string) =>
      dispatch(graphSlice.actions.setSelectedTemplate({ dashboardId, panelId: panelKey, template })),
    setDisabledItems: (itemId: string, defaultDisabled?: boolean) =>
      dispatch(graphSlice.actions.setDisabledItems({ dashboardId, panelId: panelKey, itemId, defaultDisabled })),
    setOnlyItem: (itemId: string, allItemIds: string[]) =>
      dispatch(graphSlice.actions.setOnlyItem({ dashboardId, panelId: panelKey, itemId, allItemIds })),
    enableItems: (itemIds: string[]) =>
      dispatch(graphSlice.actions.enableItems({ dashboardId, panelId: panelKey, itemIds })),
  }), [dispatch, dashboardId, panelKey])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...graphState,
    isGraphInitialized,
    ...graphUpdaters,
  }), [core, modelId, trade, graphState, isGraphInitialized, graphUpdaters])
}
